import { useMemo } from 'react';

/**
 * Custom hook to calculate column widths based on size (ratio), minWidth, and staticWidth
 * For table-layout: fixed, we need to calculate actual pixel widths
 *
 * @param {Array} headers - Array of table headers from TanStack Table
 * @param {Object} columnSizing - Object containing resized column sizes
 * @returns {Object} Object containing calculated widths, totalSize, usePixels, and hasResized flags
 */
export function useColumnWidths(headers, columnSizing) {
  return useMemo(() => {
    if (!headers.length) {
      return { widths: [], totalSize: 0 };
    }

    const widths = [];
    let totalSize = 0;
    let hasBeenResized = false;

    headers.forEach((header) => {
      const currentSize = header.getSize();
      const { columnDef } = header.column;
      const defaultSize = columnDef.size || 100; // Default ratio

      const { staticWidth, minWidth } = columnDef;
      const DEFAULT_MIN_WIDTH = 30;

      let minSize;
      if (staticWidth !== undefined) {
        minSize = staticWidth;
      } else if (minWidth !== undefined) {
        minSize = minWidth;
      } else {
        minSize = DEFAULT_MIN_WIDTH;
      }

      const isResized = columnSizing[header.column.id] !== undefined;
      if (isResized) {
        hasBeenResized = true;
      }

      const size =
        staticWidth !== undefined ? staticWidth : currentSize || defaultSize;

      widths.push({
        header,
        size,
        minSize,
        isResized,
        isStatic: staticWidth !== undefined,
      });
      totalSize += size;
    });

    // Calculate total size of non-resized, non-static columns for percentage calculation
    let nonResizedTotal = 0;
    widths.forEach(({ size, isResized, isStatic }) => {
      if (!isResized && !isStatic) {
        nonResizedTotal += size;
      }
    });

    const calculatedWidths = widths.map(
      ({ header, size, minSize, isResized, isStatic }) => {
        if (isStatic) {
          // Static columns always use their staticWidth
          return {
            header,
            width: minSize, // staticWidth is stored in minSize
            minSize,
            percentage: null,
            isResized: false,
            isStatic: true,
          };
        }

        if (isResized) {
          // Use actual pixel width from getSize() when column is resized
          const pixelWidth = Math.max(minSize, size);
          return {
            header,
            width: pixelWidth,
            minSize,
            percentage: null,
            isResized: true,
            isStatic: false,
          };
        }

        // For non-resized, non-static columns: calculate percentage-based width
        // They share the remaining space proportionally
        const percentage =
          nonResizedTotal > 0
            ? (size / nonResizedTotal) * 100
            : 100 / widths.filter((w) => !w.isResized && !w.isStatic).length;

        return {
          header,
          width: null,
          minSize,
          percentage,
          isResized: false,
          isStatic: false,
        };
      },
    );

    return {
      widths: calculatedWidths,
      totalSize,
      usePixels: hasBeenResized,
      hasResized: hasBeenResized,
    };
  }, [headers, columnSizing]);
}
